import { isKey } from '../../utils'
import { UiTheme } from '../../theme'

import { sizes as loaderSizes } from '../loader'

const borderWidth = '1px'

export default (theme: UiTheme) => {
  const getAccentColor = (key: string) => (isKey(theme.colors.accents, key) ? theme.colors.accents[key] : undefined)

  return {
    buttonGroup: {
      base: {
        '& button': {
          flex: 1,
          width: '100%',
          marginBottom: 0,
          marginRight: '0 !important',

          ':not(:first-child)': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: 0,
          },

          ':not(:last-child)': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      },
    },
    buttonToolbar: {
      base: {
        '& button': {
          flex: 1,
          width: '100%',
          marginBottom: 0,

          ':not(:last-child)': {
            marginRight: theme.settings.spacing.small,
          },
        },
      },
    },
    button: {
      base: {
        ...theme.typography.body,
        fontWeight: theme.settings.typography.weights.semibold,
        paddingTop: 0,
        paddingBottom: 0,
        paddingRight: theme.settings.spacing.medium,
        paddingLeft: theme.settings.spacing.medium,
        color: '#ffffff',
        border: `${borderWidth} solid transparent`,
        borderRadius: theme.settings.borderRadiuses.regular,
        transition: `color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out, width ${theme.settings.transitions.regularSlow}`,
        whiteSpace: 'nowrap',
        marginBottom: theme.settings.spacing.large,
        boxSizing: 'border-box',

        '.intent-mouse &:focus': {
          outline: 'none',
          boxShadow: 'none',
        },
      },

      sizes: {
        xSmall: {
          paddingLeft: '0px',
          paddingRight: '0px',
        },
      },

      flush: {
        marginBottom: 0,
      },

      clickable: {
        cursor: 'pointer',
      },

      disabled: {
        backgroundColor: '#cccccc',
      },

      multiChoice: {
        // This gives consistent padding on both sides. Needed because
        // of the bounding box of the icon
        paddingRight: theme.settings.spacing.small,
      },

      variants: {
        ...['primary', 'cta', 'positive', 'negative', 'alert', 'brand'].reduce(
          (acc, name) =>
            Object.assign(acc, {
              [name]: {
                base: {
                  backgroundColor: getAccentColor(`${name}`),
                  borderColor: getAccentColor(`${name}`),
                  outline: 'none',
                  ':hover': {
                    color: '#ffffff',
                    borderColor: getAccentColor(`${name}Hover`),
                    backgroundColor: getAccentColor(`${name}Hover`),
                  },
                  ':active': {
                    borderColor: getAccentColor(`${name}Active`),
                    backgroundColor: getAccentColor(`${name}Active`),
                  },
                  ':focus': {
                    outline: 'none',
                    boxShadow: `0 0 0 3px ${getAccentColor(`${name}Focus`)}`,
                  },
                },
                outline: {
                  color: getAccentColor(`${name}`),
                  borderColor: getAccentColor(`${name}`),
                  backgroundColor: 'transparent',
                  ':focus': {
                    backgroundColor: 'transparent',
                  },
                  ':focus:hover': {
                    backgroundColor: getAccentColor(`${name}Hover`),
                  },
                },
              },
            }),
          {}
        ),

        // secondary button is a special child
        secondary: {
          base: {
            color: theme.colors.text.normal,
            backgroundColor: theme.colors.accents.secondaryLight,
            outline: 'none',
            border: `${borderWidth} solid ${theme.colors.accents.secondaryLightBorder}`,
            ':hover': {
              borderColor: theme.colors.accents.secondaryLightBorder,
              backgroundColor: theme.colors.accents.secondaryLightHover,
            },
            ':active': {
              borderColor: theme.colors.accents.secondaryLightBorder,
              backgroundColor: theme.colors.accents.secondaryLightActive,
            },
            ':focus': {
              outline: 'none',
              boxShadow: `0 0 0 3px ${theme.colors.accents.secondaryLightFocus}`,
            },
          },
          outline: {
            color: theme.colors.text.normal,
            borderColor: theme.colors.text.dimmed,
            backgroundColor: 'transparent',
          },
        },

        clear: {
          base: {
            backgroundColor: 'transparent',
            color: theme.colors.text.normal,
            paddingLeft: 0,
            paddingRight: 0,
            ':hover': {
              color: theme.colors.text.normalHover,
            },
            ':active': {
              color: theme.colors.text.normalActive,
            },
          },
          outline: {},
        },

        clearInverted: {
          base: {
            backgroundColor: 'transparent',
            color: theme.colors.text.light,
            paddingLeft: 0,
            paddingRight: 0,
            ':hover': {
              color: theme.colors.text.lightHover,
            },
            ':active': {
              color: theme.colors.text.lightActive,
            },
          },
          outline: {},
        },

        hollowInverted: {
          base: {
            backgroundColor: 'transparent',
            color: theme.colors.text.light,
            border: `${borderWidth} solid rgba(255,255,255,0.3)`,
            outline: 'none',
            ':hover': {
              borderColor: theme.colors.text.light,
            },
            ':active': {
              color: theme.colors.text.lightActive,
            },
            ':focus': {
              outline: 'none',
              borderColor: 'transparent',
              boxShadow: `0 0 0 3px ${theme.colors.text.light}`,
            },
          },
          outline: {},
        },

        link: {
          base: {
            backgroundColor: 'transparent',
            fontWeight: theme.settings.typography.weights.semibold,
            paddingLeft: 0,
            paddingRight: 0,
            color: theme.colors.accents.primary,
            ':hover': {
              color: theme.colors.accents.primaryHover,
            },
            ':active': {
              color: theme.colors.accents.primaryActive,
            },
          },
          outline: {},
        },
      },

      block: {
        flex: 1,
        width: '100%',
      },
    },

    innerWrapper: {
      base: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '50px',
        boxSizing: 'border-box',
        marginTop: `-${borderWidth}`,
        marginBottom: `-${borderWidth}`,
      },

      variants: {
        secondaryLight: {
          marginTop: `-${borderWidth}`,
          marginBottom: `-${borderWidth}`,
        },
        secondary: {
          marginTop: `-${borderWidth}`,
          marginBottom: `-${borderWidth}`,
        },
      },

      sizes: {
        large: {
          paddingTop: theme.settings.spacing.small,
          paddingBottom: theme.settings.spacing.small,
          height: '40px',
          minWidth: '40px',
        },
        small: {
          paddingTop: '5px',
          paddingBottom: '5px',
          height: '30px',
          minWidth: '30px',
        },
        xSmall: {
          paddingTop: '0px',
          paddingBottom: '0px',
          height: '20px',
          minWidth: 'none',
        },
        flat: {
          paddingTop: 0,
          paddingBottom: 0,
          minWidth: 'none',
        },
      },

      multiChoice: {
        justifyContent: 'space-between',
      },
    },

    contentWrapper: {
      base: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },

    chevronIcon: {
      base: {
        display: 'inline-block',
        width: '20px',
        height: '20px',
        verticalAlign: 'top',
      },

      'direction-up': {
        transform: 'rotate(180deg)',
      },
    },

    processing: {
      base: {
        width: 0,
        overflow: 'hidden',
        opacity: 0,
        transition: theme.settings.transitions.regular,
        display: 'flex',
      },

      visible: {
        width: loaderSizes.small,
        opacity: 1,
      },

      visibleWithSiblings: {
        marginLeft: theme.settings.spacing.small,
      },
    },

    loader: {
      base: {
        verticalAlign: 'middle',
      },
    },
  }
}
